import React, { Fragment, memo } from 'react';
import type { FormikActions } from 'formik';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Container, Col, Row } from 'reactstrap';

import Preferences from './Preferences';
import BookSection from './BookSection';

import {
  User,
  Sticky,
  Loading,
  Toggler,
  WhitePanel,
  CostFooter,
  SemiTransparentLoader,
} from '../../../../components';
import { GuideCard } from '../../../../components/Cards';
import { getCustomToast } from '../../../../components/ErrorMessage';

import { bookGuidedTrek } from '../../../../redux/actions/trekPaymentReviewActions';
import { useDocumentTitle, useEffectOnFirstRender } from '../../../../hooks';
import { getSelectedTrekById } from '../../../../redux/actions/selectedTrekActions';
import { trackTrekPaymentReviewPageview } from '../../../../redux/actions/analyticsEventActions';

import '../../trekbuyreview.css';

type Values = {
  pax: number,
  paymentPlan: string,
  promocode: string | null,
  discount: string | null,
};

type Props = {
  trek: Object,
  fetchTrek: () => void,
  onBook: (values: Values) => void,
  trackPageView: () => void,
};

const mapActionProps = ({ selectedTrek: { trek } }) => ({
  trek,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchTrek: () => dispatch(getSelectedTrekById(ownProps.trekId)),
  trackPageView: () => dispatch(trackTrekPaymentReviewPageview()),
  onBook: (values: Values) => dispatch(bookGuidedTrek(values)),
});

const INITIAL_VALUES = { pax: 1, paymentPlan: 'R', promocode: null, discount: null };

function Review(props: Props) {
  useEffectOnFirstRender(() => {
    const { trek, trackPageView, fetchTrek } = props;
    trackPageView();
    if (!trek) fetchTrek();
  });

  useDocumentTitle(
    props.trek && `Trek Review | ${props.trek.name}`,
    props.trek && props.trek.name
  );

  if (!props.trek) return <Loading />;

  const { trek } = props;
  const { author: guideId = null, id: trekId } = trek;

  return (
    <Formik initialValues={INITIAL_VALUES} onSubmit={bookSubmit}>
      {({ values: { pax }, isSubmitting }) => {
        const totalPrice = Number(pax) * Number(trek.price.amount);

        return (
          <Container id="trekReview">
            <SemiTransparentLoader
              isLoading={isSubmitting}
              loadingText="Booking Your Trek"
            />
            <div className="review-holder">
              <Row>
                <div className="d-block d-lg-none">
                  <Toggler>
                    {({ on, toggle }) => (
                      <Fragment>
                        <Modal isOpen={on} toggle={toggle}>
                          <SemiTransparentLoader
                            isLoading={isSubmitting}
                            loadingText="Booking Your Trek"
                          />
                          <ModalHeader toggle={toggle}>Guide Trek Review</ModalHeader>
                          <ModalBody>
                            <div className="aside">
                              <BookSection totalPrice={totalPrice} />
                            </div>
                          </ModalBody>
                        </Modal>
                        <CostFooter currency="USD" onClick={toggle} amount={totalPrice} />
                      </Fragment>
                    )}
                  </Toggler>
                </div>
                <Col xl="8" lg="7">
                  <WhitePanel>
                    <TrekCard name={trek.name} numberOfDays={trek.number_of_days} />
                    {guideId && <Guide id={guideId} />}
                  </WhitePanel>
                  <Preferences trekId={trekId} />
                </Col>

                <Col xl="4" lg="5" className="d-none d-lg-block">
                  <Sticky className="aside">
                    <BookSection totalPrice={totalPrice} />
                  </Sticky>
                </Col>
              </Row>
            </div>
          </Container>
        );
      }}
    </Formik>
  );

  async function bookSubmit(values: Values, actions: FormikActions) {
    try {
      await props.onBook(values);
    } catch (e) {
      getCustomToast(
        'Some error occurred during booking your trek. Contact us or try again later',
        'error'
      );
    } finally {
      actions.setSubmitting(false);
    }
  }
}

const Guide = memo(({ id }: { id: string }) => (
  <WhitePanel>
    <h2 className="review-title">YOUR GUIDE</h2>
    <User userId={id}>
      {({ userData }) => (
        <Col md="5" sm="6">
          <GuideCard cardData={userData} isReviewCard />
        </Col>
      )}
    </User>
  </WhitePanel>
));

const TrekCard = memo(({ name, numberOfDays }: { name: string, numberOfDays: number }) => (
  <WhitePanel className="trekreview">
    <h2 className="trekreview-title">
      {name} - {numberOfDays} DAYS [REVIEW]
    </h2>
    <p>
      Please confirm the list of services included in the Guided treks before purchasing.
      <br />
      If you have any queries please reach out to the Guide that runs the trek.
      <hr />
      If you do need to contact HoneyGuide for any questions regarding this trek, please
      contact Dakshina at{' '}
      <a href="mailto:honeyhunter@honeyguideapps.com">honeyhunter@honeyguideapps.com</a>.
    </p>
  </WhitePanel>
));

export default connect(
  mapActionProps,
  mapDispatchToProps
)(Review);



// WEBPACK FOOTER //
// ./src/screens/Trek/Guided/Review/Review.js